import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { HiOutlineOfficeBuilding, HiOutlineUser, HiPlus } from "react-icons/hi";
import { Customer } from "../../models";
import {
  findPotentialCustomerMatches,
  searchCustomers,
} from "../../utils/customerMatchingUtils";

interface CustomerSelectionStepProps {
  customers: Customer[];
  orderCustomer: any;
  selectedCustomer: Customer | null;
  createNew: boolean;
  onCustomerSelect: (customer: Customer | null, createNew: boolean) => void;
}

export const CustomerSelectionStep: React.FC<CustomerSelectionStepProps> = ({
  customers,
  orderCustomer,
  selectedCustomer,
  createNew,
  onCustomerSelect,
}) => {
  const { t } = useTranslation(["orders", "common"]);
  const [searchText, setSearchText] = useState<string>("");

  // Find potential matches
  const potentialMatches = useMemo(
    () => findPotentialCustomerMatches(customers, orderCustomer),
    [customers, orderCustomer],
  );

  // Filter customers based on search text
  const searchResults = useMemo(() => {
    if (!searchText || searchText.length === 0) {
      return potentialMatches;
    }
    return searchCustomers(customers, searchText);
  }, [searchText, customers, potentialMatches]);

  const handleSelectCustomer = (customer: Customer) => {
    // Toggle functionality: if customer is already selected, deselect it
    if (selectedCustomer?.id === customer.id) {
      onCustomerSelect(null, false);
    } else {
      onCustomerSelect(customer, false);
    }
  };

  const handleCreateNew = () => {
    // Toggle functionality: if "create new" is already selected, deselect it
    if (createNew) {
      onCustomerSelect(null, false);
    } else {
      onCustomerSelect(null, true);
    }
  };

  const orderCustomerName = Customer.getCustomerName(orderCustomer);

  return (
    <div className="space-y-6">
      {/* Show order customer info */}
      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
        <h4 className="text-sm font-medium text-text mb-2">
          {t("orders:customer_from_order")}
        </h4>
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-200 dark:bg-gray-600">
            {orderCustomer?.isCompany ? (
              <HiOutlineOfficeBuilding className="w-4 h-4 text-gray-600 dark:text-gray-300" />
            ) : (
              <HiOutlineUser className="w-4 h-4 text-gray-600 dark:text-gray-300" />
            )}
          </div>
          <div>
            <div className="font-medium text-text">
              {orderCustomerName}
            </div>
            <div className="text-sm text-muted">
              {Customer.getCustomerEmail(orderCustomer) || t("common:no_email")}
            </div>
            {orderCustomer?.organizationNumber && (
              <div className="text-sm text-muted">
                {t("common:org_number")}: {orderCustomer.organizationNumber}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Potential matches section */}
      {potentialMatches.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-text mb-3">
            {t("orders:potential_matches")} ({potentialMatches.length})
          </h4>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {potentialMatches.map((customer: Customer) => (
              <div
                key={customer.id}
                onClick={() => handleSelectCustomer(customer)}
                className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                  selectedCustomer?.id === customer.id
                    ? "border-brand bg-brand-subtle dark:bg-brand-muted/20 dark:border-brand"
                    : "border-border hover:bg-gray-50 dark:hover:bg-gray-700"
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div
                    className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      selectedCustomer?.id === customer.id
                        ? "border-brand bg-brand"
                        : "border-gray-300 dark:border-gray-500"
                    }`}
                  >
                    {selectedCustomer?.id === customer.id && (
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    )}
                  </div>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-200 dark:bg-gray-600">
                    {customer.isCompany ? (
                      <HiOutlineOfficeBuilding className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                    ) : (
                      <HiOutlineUser className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-text">
                      {Customer.getCustomerName(customer)}
                    </div>
                    <div className="text-sm text-muted">
                      {Customer.getCustomerEmail(customer) || t("common:no_email")}
                    </div>
                    {customer.organizationNumber && (
                      <div className="text-sm text-muted">
                        {t("common:org_number")}: {customer.organizationNumber}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search for other customers */}
      <div>
        <h4 className="text-sm font-medium text-text mb-3">
          {t("orders:search_other_customers")}
        </h4>
        <input
          type="text"
          className="w-full border border-border-strong rounded-lg px-3 py-2 text-sm bg-surface-input text-text placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand"
          placeholder={t("common:search_customer")}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          autoComplete="off"
        />

        {searchText && (
          <div className="mt-2 max-h-48 overflow-y-auto border border-border rounded-lg bg-surface-input">
            {searchResults.length === 0 ? (
              <div className="p-4 text-center text-muted">
                {t("orders:no_customers_found")}
              </div>
            ) : (
              searchResults.map((customer: Customer) => (
                <div
                  key={customer.id}
                  onClick={() => handleSelectCustomer(customer)}
                  className={`p-3 border-b border-gray-100 dark:border-gray-600 last:border-b-0 cursor-pointer transition-colors ${
                    selectedCustomer?.id === customer.id
                      ? "bg-brand-muted dark:bg-brand-muted/20 border-brand-muted dark:border-brand"
                      : "hover:bg-gray-50 dark:hover:bg-gray-600"
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div
                      className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        selectedCustomer?.id === customer.id
                          ? "border-brand bg-brand"
                          : "border-gray-300 dark:border-gray-500"
                      }`}
                    >
                      {selectedCustomer?.id === customer.id && (
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      )}
                    </div>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-200 dark:bg-gray-600">
                      {customer.isCompany ? (
                        <HiOutlineOfficeBuilding className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                      ) : (
                        <HiOutlineUser className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-text">
                        {Customer.getCustomerName(customer)}
                      </div>
                      <div className="text-sm text-muted">
                        {Customer.getCustomerEmail(customer) || t("common:no_email")}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Create new customer option */}
      <div>
        <button
          type="button"
          onClick={handleCreateNew}
          className={`w-full p-3 border rounded-lg cursor-pointer transition-colors ${
            createNew
              ? "border-brand bg-brand-subtle dark:bg-brand-muted/20 dark:border-brand"
              : "border-border hover:bg-gray-50 dark:hover:bg-gray-700"
          }`}
        >
          <div className="flex items-center space-x-3">
            <div
              className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                createNew
                  ? "border-brand bg-brand"
                  : "border-gray-300 dark:border-gray-500"
              }`}
            >
              {createNew && (
                <div className="w-2 h-2 bg-white rounded-full"></div>
              )}
            </div>
            <div className="w-8 h-8 rounded-full flex items-center justify-center bg-green-100 dark:bg-green-900/20">
              <HiPlus className="w-4 h-4 text-green-600 dark:text-green-400" />
            </div>
            <div className="text-left">
              <div className="font-medium text-text">
                {t("orders:create_new_customer")}
              </div>
              <div className="text-sm text-muted">
                {t("orders:create_new_customer_description")}
              </div>
            </div>
          </div>
        </button>
      </div>
    </div>
  );
};
